import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Grid, Card, CardContent, CircularProgress, LinearProgress, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { advancedAnalyticsAPI } from '../services/api';

const Reports = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geoHotspots, setGeoHotspots] = useState([]);
  const [districtDetailed, setDistrictDetailed] = useState([]);
  const [departmentPerformance, setDepartmentPerformance] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setLoading(true);
        const [hotspots, districts, depts] = await Promise.all([
          advancedAnalyticsAPI.getGeographicHotspots(),
          advancedAnalyticsAPI.getDistrictDetailedAnalytics(),
          advancedAnalyticsAPI.getDepartmentPerformance()
        ]);

        if (hotspots.data.success) setGeoHotspots(hotspots.data.data.rows || []);
        if (districts.data.success) setDistrictDetailed(districts.data.data.rows || []);
        if (depts.data.success) setDepartmentPerformance(depts.data.data.rows || []);
      } catch (err) {
        setError('Error loading reports: ' + err.message);
        console.error('Reports error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, []);

  const reportTypes = [
    { title: 'Total Complaints', count: geoHotspots.reduce((sum, d) => sum + parseInt(d.complaint_density || 0), 0), color: '#667eea' },
    { title: 'Districts Covered', count: geoHotspots.length, color: '#764ba2' },
    { title: 'Avg Resolution Rate', count: (geoHotspots.reduce((sum, d) => sum + parseFloat(d.local_resolution_rate || 0), 0) / (geoHotspots.length || 1)).toFixed(1) + '%', color: '#f093fb' },
    { title: 'Hotspot Areas', count: geoHotspots.filter(d => parseInt(d.complaint_density) > 200).length, color: '#4facfe' }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, px: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            mb: 1
          }}
        >
          Reports
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.95rem'
          }}
        >
          Generate complaints reports and view geographical distribution
        </Typography>
      </Box>

      {/* Report Types Cards */}
      <Grid container spacing={2} sx={{ mb: 3, px: 1 }}>
        {reportTypes.map((report, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card
              sx={{
                borderLeft: `4px solid ${report.color}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ color: '#9ca3af', mb: 1 }}>
                  {report.title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: report.color }}>
                  {report.count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs Section */}
      <Paper elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2, mx: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid #e5e7eb`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#6b7280',
              padding: '12px 20px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                color: '#667eea',
                bgcolor: '#f9fafb'
              }
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#667eea',
              fontWeight: 600
            },
            '& .MuiTabIndicator-root': {
              backgroundColor: '#667eea',
              height: 2
            }
          }}
        >
          <Tab label="Geographical Hotspots" />
          <Tab label="District Breakdown" />
          <Tab label="Department Distribution" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Geographical Hotspots Tab */}
              {tabValue === 0 && (
                <Box>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Complaint Density by Geographic Area</Typography>
                  <Grid container spacing={2}>
                    {geoHotspots.map((spot, idx) => {
                      const maxDensity = Math.max(...geoHotspots.map(s => parseInt(s.complaint_density)));
                      const densityPercent = (parseInt(spot.complaint_density) / maxDensity) * 100;
                      const color = densityPercent > 70 ? '#ef4444' : densityPercent > 40 ? '#f59e0b' : '#10b981';

                      return (
                        <Grid item xs={12} md={6} key={idx}>
                          <Paper
                            sx={{
                              p: 2,
                              border: '1px solid #e5e7eb',
                              borderRadius: 1,
                              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1.5 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {spot.district_name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {parseInt(spot.complaint_density)} total complaints
                                </Typography>
                              </Box>
                              <Chip
                                label={`${spot.local_resolution_rate}%`}
                                sx={{ bgcolor: '#e0f2fe', color: '#0284c7', fontWeight: 600 }}
                              />
                            </Box>

                            <LinearProgress
                              variant="determinate"
                              value={densityPercent}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: color,
                                  borderRadius: 4
                                },
                                mb: 1.5
                              }}
                            />

                            <Grid container spacing={1} sx={{ mb: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Resolved</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{spot.resolved_here}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Last 30 Days</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{spot.last_30_days}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Categories</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{spot.category_diversity}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Latest Report</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                  {new Date(spot.latest_complaint).toLocaleDateString()}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* District Breakdown Tab */}
              {tabValue === 1 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Detailed District Analytics</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                        <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Complaints</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Users</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>This Month</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {districtDetailed.slice(0, 37).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.district_name}</TableCell>
                          <TableCell align="right">{row.total_complaints || 0}</TableCell>
                          <TableCell align="right" sx={{ color: row.resolution_rate > 70 ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                            {row.resolution_rate || 0}%
                          </TableCell>
                          <TableCell align="right">{row.unique_users || 0}</TableCell>
                          <TableCell align="right">{row.avg_days_open || 0}</TableCell>
                          <TableCell align="right">{row.complaints_this_month || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* Department Distribution Tab */}
              {tabValue === 2 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Department-wise Distribution</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>This Week</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>This Month</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Districts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departmentPerformance.slice(0, 20).map((row, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.department_name}</TableCell>
                          <TableCell align="right">{row.total_complaints || 0}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ bgcolor: '#dbeafe', p: '4px 8px', borderRadius: 1, display: 'inline-block', color: '#0284c7', fontWeight: 600 }}>
                              {row.resolution_rate || 0}%
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.complaints_this_week || 0}</TableCell>
                          <TableCell align="right">{row.complaints_this_month || 0}</TableCell>
                          <TableCell align="right">{row.districts_covered || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Reports;
